import { io, type Socket } from 'socket.io-client';

import { SERVER_EVENTS, CLIENT_EVENTS } from '@alfin-crm/contracts';
import { API_URL } from '@/lib/env';

let socket: Socket | null = null;
let lastSocketToken: string | null = null;
let getAccessToken: (() => string | null) | null = null;

/**
 * Register a getter so reconnect attempts always use the latest in-memory JWT.
 */
export function registerSocketTokenProvider(provider: () => string | null): void {
  getAccessToken = provider;
}

function resolveAccessToken(explicit?: string | null): string | null {
  if (explicit) return explicit;
  return getAccessToken?.() ?? null;
}

function attachSocketListeners(s: Socket): void {
  // Socket.IO reuses handshake auth on auto-reconnect — refresh JWT before each attempt.
  s.io.on('reconnect_attempt', () => {
    const token = resolveAccessToken();
    if (token) {
      s.auth = { token };
    }
  });

  s.on('connect', () => {
    console.warn('[Socket] Connected:', s.id);
  });

  s.on('disconnect', (reason) => {
    console.warn('[Socket] Disconnected:', reason);
  });

  s.on('connect_error', (err) => {
    console.error('[Socket] Connection error:', err.message);
  });
}

/**
 * Returns the singleton Socket.IO client.
 * Creates the connection lazily on first call.
 */
export function getSocket(): Socket {
  if (!socket) {
    socket = io(API_URL, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      withCredentials: true,
      autoConnect: false,
    });
    attachSocketListeners(socket);
  }
  return socket;
}

/**
 * Update socket auth and connect / reconnect when the access token changes.
 * Call after login, token refresh, or when Redux auth state updates.
 */
export function syncSocketAuth(accessToken?: string | null): void {
  const token = resolveAccessToken(accessToken);
  if (!token) {
    disconnectSocket();
    return;
  }

  const s = getSocket();
  const tokenChanged = lastSocketToken !== null && lastSocketToken !== token;
  lastSocketToken = token;
  s.auth = { token };

  if (s.connected) {
    if (tokenChanged) {
      s.disconnect();
      s.connect();
    }
    return;
  }

  s.connect();
}

/** @deprecated Use syncSocketAuth — kept for existing call sites */
export function connectSocket(accessToken?: string | null): void {
  syncSocketAuth(accessToken);
}

/** Disconnect and destroy the socket (call on logout) */
export function disconnectSocket(): void {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
  lastSocketToken = null;
}

// Re-export event names for use in components
export { SERVER_EVENTS, CLIENT_EVENTS };
